// @ts-ignore - allow import with query string
import { setIsLoading } from './util.js?v=25';
import { fetchFulfillmentLocationAvailability } from './fulfillmentLocationAvailability.js?v=4';

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      connectedCallback() {
        if (!this.resizeObserver) {
          this.resizeObserver = new ResizeObserver((entries) => {
            for (let entry of entries) {
              const h = Math.round(entry.contentRect.height);
              document.documentElement.style.setProperty('--product-form-height', `${h}px`);
            }
          });
        }

        this.resizeObserver.observe(this);

        this.onLoaded();
      }

      onLoaded() {
        this.enableProductWizardTriggers();
      }

      disconnectedCallback() {
        if (this.resizeObserver) {
          this.resizeObserver.unobserve(this);
        }
      }

      constructor() {
        super();

        /**
         * @type {HTMLFormElement | null}
         */
        this.form = this.querySelector('form');
        this.button = this.querySelector('.product-form__button');

        /**
         * @type {HTMLElement | null}
         */
        this.errorMessage = this.querySelector('.product-form__error-message');
        this.scrollTrigger = this.querySelector('[js-product-form-scroll-trigger]');

        this.CLASSES = {
          headerCollapsed: 'product-form__header--is-collapsed',
          buttonLoadingClass: 'button--is-loading',
          buttonErrorClass: 'button--has-error',
        };

        this.MESSAGES = {
          selectSize: this.errorMessage?.dataset.messageSelectSize,
          noStock: this.errorMessage?.dataset.messageNoStock,
        };

        this.elements.ctas.forEach((cta) => {
          cta.addEventListener('click', this.onSubmitHandler.bind(this));
        });

        this.form?.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.form?.addEventListener('change', this.onChangeHandler.bind(this));

        this.scrollTrigger?.addEventListener('click', this.handleScroll.bind(this));

        this.style.setProperty('--product-form-mb', this.marginBottom);

        if (this.fulfillmentLocation && this.productId) {
          fetchFulfillmentLocationAvailability(
            this.productId,
            this.fulfillmentLocation,
            this.isWarehouseFulfillmentLocation,
          );
        }
      }

      onChangeHandler() {
        this.resetButton();
      }

      /**
       *
       * @param {SubmitEvent|MouseEvent} event
       */
      onSubmitHandler(event) {
        event.preventDefault();

        if (this.productWizardTriggers.length) return;

        this.setLoadingButton();
        this.disableButton();

        if (!this.hasSelectedVariant) {
          this.MESSAGES.selectSize && this.setErrorButton(this.MESSAGES.selectSize);
          return;
        }

        fetch(`${window.routes.cartAddUrl}`, this.fetchConfig)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.MESSAGES.noStock && this.setErrorButton(this.MESSAGES.noStock);
              this.disableVariant();
            } else {
              document.dispatchEvent(new Event('addedToCart'));
              this.resetButton();
              window.location.href = '/cart';
            }
          })
          .catch((e) => {
            console.error(e);
          });
      }

      disableVariant() {
        if (!this.form) return;

        /**
         * @type {HTMLInputElement | null}
         */
        const inputId = this.form?.querySelector('input[name="id"]:checked');

        this.hasSelectedVariant && inputId?.setAttribute('disabled', 'true');
      }

      disableButton() {
        this.button?.setAttribute('disabled', 'true');
      }

      enableProductWizardTriggers() {
        this.productWizardTriggers.forEach((trigger) => {
          trigger.removeAttribute('disabled');
        });
      }

      /**
       * @param {String} message
       */
      setErrorButton(message) {
        if (this.errorMessage) {
          this.errorMessage.textContent = message;
        }

        this.button?.classList.add(this.CLASSES.buttonErrorClass);
        this.removeLoadingButton();
      }

      setLoadingButton() {
        this.button?.classList.add(this.CLASSES.buttonLoadingClass);
        setIsLoading(true);
      }

      removeLoadingButton() {
        this.button?.classList.remove(this.CLASSES.buttonLoadingClass);
        setIsLoading(false);
      }

      resetButton() {
        this.button?.removeAttribute('disabled');
        this.button?.classList.remove(this.CLASSES.buttonErrorClass);
        this.removeLoadingButton();
      }

      handleScroll() {
        window.scrollTo({
          top: window.innerHeight,
          behavior: 'smooth',
        });
      }

      get productWizardTriggers() {
        return document.querySelectorAll('[js-product-wizard-trigger]');
      }

      get sectionsToRender() {
        return [
          {
            id: 'header',
            section: 'header',
            selector: '.js-content',
          },
          {
            id: 'cart-items',
            section: 'cart-items',
            selector: '.js-content',
          },
          {
            id: 'cart-summary',
            section: 'cart-summary',
            selector: '.js-content',
          },
        ];
      }

      get fetchConfig() {
        if (!this.form) return;

        const formData = new FormData(this.form);

        return {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: `application/javascript` },
          body: formData,
        };
      }

      get hasSelectedVariant() {
        if (!this.form) return;

        /**
         * @type {HTMLInputElement | null}
         */
        const inputId = this.form.querySelector('input[name="id"]');

        return inputId?.value !== '';
      }

      get elements() {
        return {
          ctas: document.querySelectorAll('[js-product-form-cta]'),
          header: this.querySelector('.product-form__header'),
          /**
           * @type {HTMLElement | null}
           */
          marginBottom: document.querySelector('[js-product-form-margin-bottom]'),
        };
      }

      get marginBottom() {
        return `${this.elements.marginBottom?.offsetHeight}px`;
      }

      get height() {
        return `${this?._height}px`;
      }

      get productId() {
        return this.dataset.productId;
      }

      /**
       * @returns {string | false}
       */
      get fulfillmentLocation() {
        return this.getAttribute('js-fulfillment-location') ?? false;
      }

      /**
       * @returns {boolean}
       */
      get isWarehouseFulfillmentLocation() {
        return this.getAttribute('js-fulfillment-location-is-warehouse') === 'true';
      }
    },
  );
}
